import {
    CanActivateFn,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    UrlTree,
    Router,
} from '@angular/router';
import { inject } from '@angular/core';
import { SettingsService } from '@core/settings/settings.service';
import { CartService } from '@core/cart/cart.service';
import { map, catchError, of, Observable } from 'rxjs';
import { CookiesService } from '@enervee/webapp-common';

/**
 * This guard handles the marketplace → cart-app handoff carried in the query string,
 * then makes a request to get all settings and redirects all routes from the
 * cartapp_blocked_routes setting to /404.
 */
export const blockUrlGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const cookiesService = inject(CookiesService);
    const params = route.queryParamMap;
    if (params.keys.length) {
        const claimToken = params.get('claimToken');
        const cartId = params.get('cartId');
        if (params.get('currentUtility')) {
            cookiesService.set('currentUtility', params.get('currentUtility'));
        }
        const stripHandoffParams = (): UrlTree =>
            router.createUrlTree([], {
                queryParams: {
                    cartId: null,
                    claimToken: null,
                    currentUtility: null,
                },
                queryParamsHandling: 'merge',
            });

        if (claimToken) {
            // Bind the handed-over cart to this session server-side. The browser never
            // writes the cart id itself, so a forged or guessed id cannot claim a cart.
            // If the claim fails (expired or reused token), continue without a cart and
            // let the cart resolver create a fresh one rather than dead-ending the user.
            const cartService = inject(CartService);
            return cartService.claimCart(claimToken).pipe(
                map(() => stripHandoffParams()),
                catchError(() => of(stripHandoffParams())),
            );
        }
        if (cartId) {
            // Legacy handoff from marketplace builds that predate claim tokens. Root
            // stops honouring this cookie once its cart_cookie_fallback setting is off.
            cookiesService.set('standalone_cart_id', cartId);
        }
        return of(stripHandoffParams());
    }

    const settingsService = inject(SettingsService);
    return settingsService.requestSettings().pipe(
        map(() => {
            const patternFromSetting = settingsService.getSiteSetting(
                'cartapp_blocked_routes',
            ) as string[];

            const isBlocked = patternFromSetting.some((pattern) =>
                matchPattern(state.url, pattern),
            );
            return isBlocked
                ? router.createUrlTree(['/404'], {
                      queryParamsHandling: 'merge',
                  })
                : true;
        }),
        catchError(() => {
            return of(
                router.createUrlTree(['/404'], {
                    queryParamsHandling: 'merge',
                }),
            );
        }),
    );
};

/**
 * Compare url with the pattern that includes wildcard
 */
const matchPattern = (url: string, pattern: string): boolean => {
    const patternSegments = pattern.split('/');
    const urlSegments = url.split('/');
    return (
        patternSegments.length === urlSegments.length &&
        patternSegments.reduce((matched, segment, index) => {
            if (segment === '**') return matched;
            return matched && segment === urlSegments[index];
        }, true)
    );
};
